/**
 * Question card component with header and content support.
 *
 * @unstable
 */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  inject,
} from '@angular/core';
import { SurveyStore } from '@player/shared/services/survey-store.service';
import { QuestionData } from '@shared/models/survey.model';
import { filter, map, take } from 'rxjs/operators';
import { SharedModuleForPlayer } from '@player/shared/modules/shared.module';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { BasicModule } from '@shared/modules/basic.module';
import { SliderAnswer } from '@player/shared/pipes/slider-answer.pipe';
import { MediaCtaComponent } from '@player/shared/components/media-cta/media-cta.component';
import { LabelOverflow } from '@player/shared/directives/label-overflow.directive';
import { SafeHtml } from '@shared/pipes/safe-html.pipe';
import { Slider2D } from '@player/+survey/question/slider-2d/slider-2d.component';
import { Slider1D } from '@player/+survey/question/slider-1d/slider-1d.component';
import { SliderNps } from '@player/+survey/question/slider-nps/slider-nps.component';
import { SliderENps } from '@player/+survey/question/slider-nps/slider-e-nps.component';
import { Slider1R } from '@player/+survey/question/slider-1r/slider-1r.component';
import { InputInfo } from '@player/+survey/question/input-info/input-info.component';
import { InputDropdown } from '@player/+survey/question/input-dropdown/input-dropdown.component';
import { InputChoice } from '@player/+survey/question/input-choice/input-choice.component';
import { InputChoicePicture } from '@player/+survey/question/input-choice-picture/input-choice-picture.component';
import { InputCheckbox } from '@player/+survey/question/input-checkbox/input-checkbox.component';
import { InputPhone } from '@player/+survey/question/input-phone/input-phone.component';
import { FileUpload } from '@player/+survey/question/file-upload/file-upload.component';
import { AIInterviewer } from '@player/+survey/question/ai-interviewer/ai-interviewer.component';
import { WhyFinder } from '@player/+survey/question/why-finder/why-finder.component';
import { InputSimple } from '@player/+survey/question/input-simple/input-simple.component';
import { Questions } from '@shared/enums/questions.enum';

@Component({
  selector: 'question-card',
  templateUrl: './question-card.component.html',
  styleUrls: ['./question-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SharedModuleForPlayer,
    AsyncPipe,
    BasicModule,
    NgClass,
    SliderAnswer,
    MediaCtaComponent,
    LabelOverflow,
    SafeHtml,
    Slider2D,
    Slider1D,
    SliderNps,
    SliderENps,
    Slider1R,
    InputInfo,
    InputDropdown,
    NgTemplateOutlet,
    InputChoice,
    InputChoicePicture,
    InputCheckbox,
    InputPhone,
    FileUpload,
    AIInterviewer,
    WhyFinder,
    InputSimple,
  ],
})
export class QuestionCard implements OnChanges, AfterViewInit {
  readonly cdRef = inject(ChangeDetectorRef);
  readonly elRef = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly ss = inject(SurveyStore);

  readonly Questions = Questions;

  private answerTimer: number = 300;
  private answerTimeout: number = null;

  readonly groupData = this.ss.questions.pipe(
    map((questions) => Questions.getGroup(this.questionData, questions)),
    filter((group) => group && group.type === Questions.GROUP_SCORED),
  );

  @Input() no: number = 0;

  @Input() active: boolean = false;
  @Input() visible: boolean = true;

  @Input() isLast: boolean = false;
  @Input() isFirst: boolean = false;

  @Input() answerValue: string = null;

  @Input() questionData: QuestionData = null;

  @Output() readonly nextQuestion = new EventEmitter<any>();
  @Output() readonly prevQuestion = new EventEmitter<any>();

  @Output() readonly answerReady = new EventEmitter<any>();
  @Output() readonly answerChanged = new EventEmitter<any>();
  @Output() readonly activeChanged = new EventEmitter<any>();

  @Output() readonly dragChange = new EventEmitter<boolean>();

  @Output() readonly waitNext = new EventEmitter<number>();

  @ViewChildren(QuestionCard) questionCards: QueryList<QuestionCard>;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['active'] && !changes['active'].firstChange) {
      if (this.questionData) {
        this.activeChanged.emit({ item: this.questionData });
      }

      window.clearTimeout(this.answerTimeout);
    }
  }

  ngAfterViewInit() {
    const questionWrapHeight = (this.elRef.nativeElement.closest('.z-card-wrapper') as HTMLElement)?.offsetHeight;
    const playerHeight = this.ss.playerDimensions.value.height;
    const scrollable = playerHeight < questionWrapHeight;

    this.ss.updateDimensions({ scrollable });
  }

  hasMediaCta(): boolean {
    return (
      (Questions.group(this.questionData) || !!Questions.category(this.questionData, Questions.INFO)) &&
      !!(this.questionData.cta || this.questionData.videoEmbed || this.questionData.image?.url)
    );
  }

  onAnswerReady(): void {
    this.answerReady.emit({
      item: this.questionData,
    });

    this.ss.survey.pipe(take(1)).subscribe((survey) => {
      if (survey.autojump) {
        if (this.answerTimeout) {
          window.clearTimeout(this.answerTimeout);
        }

        this.answerTimeout = window.setTimeout(() => this.nextQuestion.emit(), this.answerTimer);
      }
    });
  }

  onAnswerChange(value: string): void {
    this.answerChanged.emit({
      value,
      item: this.questionData,
    });

    if (this.answerTimeout) {
      window.clearTimeout(this.answerTimeout);
    }
  }

  saveSummaries($event): void {
    for (const prop in $event || {}) {
      const modifiedQuestionData = JSON.parse(JSON.stringify(this.questionData));
      modifiedQuestionData.$key = modifiedQuestionData?.$key + `:${prop}`;

      this.answerChanged.emit({
        value: $event[prop],
        item: modifiedQuestionData,
      });
    }
  }
}
